import { logDebug } from "./log";
/**
 * TikTok Pixel — client-side event tracking.
 *
 * Activates only when REACT_APP_TIKTOK_PIXEL_ID is set in frontend/.env at
 * build time. When unset, all functions become safe no-ops so preview keeps
 * working and no ghost network requests get sent.
 *
 * Docs: https://business-api.tiktok.com/portal/docs
 */

const PIXEL_ID = (
  typeof process !== "undefined" && process.env && process.env.REACT_APP_TIKTOK_PIXEL_ID
) || "";

let initialized = false;

// Inject the TikTok Pixel snippet exactly once.
function ensureLoaded() {
  if (typeof window === "undefined" || !PIXEL_ID) return;
  if (initialized) return;
  initialized = true;

  // TikTok's official pixel bootstrap (adapted from their JS snippet).
  /* eslint-disable */
  !function (w, d, t) {
    w.TiktokAnalyticsObject = t;
    const ttq = (w[t] = w[t] || []);
    ttq.methods = ["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie","holdConsent","revokeConsent","grantConsent"];
    ttq.setAndDefer = function (t, e) {
      t[e] = function () { t.push([e].concat(Array.prototype.slice.call(arguments, 0))); };
    };
    for (let i = 0; i < ttq.methods.length; i++) ttq.setAndDefer(ttq, ttq.methods[i]);
    ttq.instance = function (t) {
      for (let e = ttq._i[t] || [], n = 0; n < ttq.methods.length; n++) ttq.setAndDefer(e, ttq.methods[n]);
      return e;
    };
    ttq.load = function (e, n) {
      const r = "https://analytics.tiktok.com/i18n/pixel/events.js";
      const o = n && n.partner;
      ttq._i = ttq._i || {};
      ttq._i[e] = [];
      ttq._i[e]._u = r;
      ttq._t = ttq._t || {};
      ttq._t[e] = +new Date();
      ttq._o = ttq._o || {};
      ttq._o[e] = n || {};
      n = document.createElement("script");
      n.type = "text/javascript";
      n.async = !0;
      n.src = r + "?sdkid=" + e + "&lib=" + t;
      e = document.getElementsByTagName("script")[0];
      e.parentNode.insertBefore(n, e);
    };
    ttq.load(PIXEL_ID);
    ttq.page();
  }(window, document, "ttq");
  /* eslint-enable */
}

function safeTrack(event, data) {
  try {
    if (!PIXEL_ID) return;
    ensureLoaded();
    if (window.ttq && typeof window.ttq.track === "function") {
      window.ttq.track(event, data || {});
    }
  } catch (error) { logDebug("TikTok track suppressed:", error?.message); }
}

// --- Public API ---
export const isTikTokPixelActive = () => Boolean(PIXEL_ID);

export const trackPageView = () => {
  try {
    if (!PIXEL_ID) return;
    ensureLoaded();
    if (window.ttq && typeof window.ttq.page === "function") {
      window.ttq.page();
    }
  } catch (error) { logDebug("TikTok pageview suppressed:", error?.message); }
};

// Fired when a visitor completes the 15-question quiz (before results shown).
export const trackQuizComplete = () => safeTrack("SubmitForm", { content_name: "breed_quiz" });

// Fired when the paid $2 unlock lands on /payment/success.
export const trackPurchase = () =>
  safeTrack("Purchase", {
    content_id: "unlock_lifetime",
    content_name: "Fetchright Unlock",
    content_type: "product",
    currency: "AUD",
    value: 2,
  });

// Fired when someone clicks any affiliate outbound (insurance, kit, vet).
export const trackAffiliateClick = (partner) =>
  safeTrack("ClickButton", { content_name: `affiliate_${partner}` });

// Attach `window.ttq.page()` on every route change (SPA-friendly).
// Call from a component that wraps the Router.
export function useTikTokRouteTracking(pathname) {
  if (typeof window === "undefined") return;
  if (!PIXEL_ID) return;
  // Fires on first mount + every route change.
  ensureLoaded();
  if (window.ttq && typeof window.ttq.page === "function") window.ttq.page();
  // Cannot use hooks here because this is a plain fn — see TikTokPixelRouter.
}
